<template>
  <div v-if="readOnly" class="flex flex-col gap-x-2">
    <label v-if="label" :for="fieldName" class="">
      <span>{{ t9nLabel }}</span>
      <HintTippy v-if="hint" :hint="hint"></HintTippy>
    </label>
    <span class="flex-1">
      <span v-if="selectedValue?.label" class="w-full">
        {{ $t(selectedValue?.label) }}
      </span>
    </span>
  </div>
  <div v-else class="w-full flex flex-col gap-y-1" :class="[fullWidth && '!w-full']">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1">
      <label v-if="label" :for="fieldName" :class="labelClass" class="flex items-center gap-x-2">
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col gap-y-1">
        <InputAppendage v-if="appendages?.top" v-bind="appendages">
          {{ $t(appendages?.top) }}
        </InputAppendage>
        <div class="w-full flex">
          <!-- if we need to bring back the two way dropdown: replace false with web?.is_two_way -->
          <div
            v-if="false"
            :class="[{ 'border-s-4 border-s-primary': isRequired && !withIsDisabled }]"
            class="p-1 w-4/12 md:w-2/12 flex items-center has-[:disabled]:bg-slate-200 bg-gray-100 border border-brand-grayblue-300 rounded-s-sm"
          >
            <input
              :disabled="withIsDisabled"
              type="text"
              class="w-full ps-3 py-1 border border-brand-grayblue-300 outline-none focus:border-primary"
              v-model="textModel"
              @blur="onValueBlur"
            />
          </div>
          <div
            :class="[
              {
                'border-s-4 border-s-primary rounded-s-sm': isRequired && !false && !withIsDisabled
              },
              { 'bg-brand-gray-250 ': withIsDisabled },
              false ? 'w-8/12 md:w-10/12' : 'w-full',
              { '!bg-inherit !border-0': isReadOnly }
            ]"
            class="p-1 w-full flex items-center border border-brand-grayblue-300"
            ref="wrapperDomRef"
          >
            <tippy
              :on-click-outside="handleBlur"
              :arrow="false"
              :offset="[-10, 10]"
              :trigger="withIsDisabled ? 'manual' : 'focus click'"
              theme="light-border"
              interactive
              placement="bottom-start"
              class="w-full flex items-center"
              max-width="none"
              @mount="setTippyWidth"
            >
              <div class="w-full flex justify-between px-3">
                <button
                  :disabled="withIsDisabled"
                  class="tippy__trigger w-full text-start line-clamp-1 truncate outline-none text-brand-black-secondary"
                  @click.prevent=""
                >
                  <span v-if="selectedValue?.label" class="w-full">
                    {{ selectedValue?.label }}
                  </span>
                  <span v-else class="w-full text-gray-400">
                    {{ t9nPlaceholder || t9nLabel }}
                  </span>
                </button>
                <div v-if="!withIsDisabled" class="flex items-center gap-x-2">
                  <button v-if="selectedValue" @click.stop.prevent="onSelection(null)">
                    <faTimes class="mt-1 fill-red-700 w-2" />
                  </button>
                  <FontAwesomeIcon :icon="faCaretDown" class="text-brand-black-secondary" />
                </div>
              </div>
              <template #content="{ hide }">
                <div class="tippy-content__container w-full p-0.5" :style="{ width: wrapperWidth }">
                  <div class="w-full flex flex-col gap-y-1">
                    <div class="px-1.5 pt-1">
                      <input
                        v-model="searchValue"
                        type="text"
                        class="w-full ps-3 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary"
                      />
                    </div>
                    <div class="max-h-[12rem] h-full overflow-y-auto">
                      <ul class="divide-y pb-2">
                        <li v-for="(option, key) in filteredOptions" :key="key" class="">
                          <button
                            class="ps-3 py-1.5 hover:bg-gray-100 w-full text-start"
                            @click.prevent="
                              () => {
                                hide()
                                onSelection(option)
                              }
                            "
                          >
                            {{ option.label }}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </template>
            </tippy>
          </div>
        </div>
        <InputAppendage
          v-if="Array.isArray(appendages?.bottom)"
          :font-weight="appendages.font_weight"
          v-bind="appendages"
        >
          <div v-for="(item, key) in appendages?.bottom" :key="key">
            {{ $t(item) }}
          </div>
        </InputAppendage>
        <InputAppendage
          v-else-if="appendages?.bottom"
          :font-weight="appendages.font_weight"
          v-bind="appendages"
        >
          {{ $t(appendages?.bottom) }}
        </InputAppendage>
      </div>
    </div>
    <div v-if="hasError" class="w-full flex flex-col md:flex-row gap-x-4 gap-y-1 md:items-center">
      <div v-if="showErrorSpacer" class="w-full md:w-1/3"></div>
      <span class="w-full pt-[2px] text-brand-red ltr:font-sans text-xs">
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
import faTimes from '@/assets/icons/faTimes.svg?component'
import HintTippy from '@/components/Form/HintTyppy.vue'
import InputAppendage from '@/components/Form/InputAppendage.vue'
import useDropdownActions from '@/composables/actions/useDropdownActions'
import useDropdownOptions from '@/composables/useDropdownOptions'
import useRuleHandler from '@/composables/useRuleHandler'
import useVisible from '@/composables/useVisible'
import useWatchAction from '@/composables/watchActions/index'
import { useFormStore } from '@/stores/form'
import { useLookupsStore } from '@/stores/lookups'
import { faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import Fuse from 'fuse.js'
import { useField } from 'vee-validate'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Tippy } from 'vue-tippy'
import { useAuthStore } from '@/stores/auth'
import { useGlobalStore } from '@/stores/global'
import lodashGet from 'lodash/get'

const props = defineProps({
  fullWidth: {
    type: Boolean,
    default: false
  },
  resetValues: Array,
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  isDisabled: [Boolean, Object],
  hint: String,
  placeholder: String,
  showRules: Object,

  options: {
    type: [Array, Object],
    required: true
  },
  defaultValue: {
    type: [Object, String]
  },
  rules: Object,
  fuzzyKeywords: {
    type: Array,
    default: () => ['label']
  },
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  on_change: {
    type: Object
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  showErrorSpacer: {
    type: Boolean,
    default: () => true
  },
  dependentOn: String,
  web: Object,
  appendages: Object,
  trigger: String,
  onChange: Object,
  onBlur: Object,
  onWatch: Object,
  watchAction: String,
  readOnly: Boolean
})

const lookupStore = useLookupsStore()
const { parseOptions, processQuery, processPathQuery, fetchOptions } = useDropdownOptions()
const formStore = useFormStore()
const { findShowRule, getBindObj } = useVisible()
const route = useRoute()

const textModel = ref()
const selectedValue = ref()
const searchValue = ref()
const fuse = ref()
const selectOptions = ref()
const i18n = useI18n()
const fieldName = toRef(props, 'fieldName')
const wrapperDomRef = ref()
const wrapperWidth = ref()

const asyncDataToLookupStore = (dataList) => {
  if (props.options.async_data) {
    lookupStore.overrideOptionsSource(props.options.async_data, dataList)
  }
}

const setTippyWidth = (instance) => {
  nextTick(() => {
    if (instance) {
      document.getElementById(`tippy-${instance.id}`).style.width =
        wrapperDomRef.value.offsetWidth + 'px'
    }
  })
}

const cleanupResize = useEventListener(
  window,
  'resize',
  useDebounceFn((e) => {
    setTippyWidth()
  }, 1000)
)

onMounted(async () => {
  setTippyWidth()

  const isDependent = props.dependentOn && !formStore.form.values[props.dependentOn]
  await nextTick()
  let options = []
  // api shouldn't be called when it has a dependency
  if (isDependent) return

  options = await fetchOptions(props.options, { ...props })

  /**
   * sometimes, extra logic needs to be made.
   * one example is service 767. On change of Person Category,
   * Person Sub Category is fetched, but needs extra handlers to filter out
   * options based on user type, and other factors.
   */
  if (props.options.api?.on_success?.action) {
    const { action, action_params } = props.options.api.on_success
    const parsedParams = { ...action_params }
    if (Object.values(parsedParams).includes('__serviceTransactionId__')) {
      Object.assign(
        parsedParams,
        JSON.parse(
          JSON.stringify(parsedParams).replaceAll(
            '__serviceTransactionId__',
            route.params.serviceId
          )
        )
      )
    }

    if (Object.values(parsedParams).includes('__requestNumber__') && route.query.requestNumber) {
      Object.assign(
        parsedParams,
        JSON.parse(
          JSON.stringify(parsedParams).replaceAll('__requestNumber__', route.query.requestNumber)
        )
      )
    }

    if (Object.values(parsedParams).includes('__applicationId__')) {
      Object.assign(
        parsedParams,
        JSON.parse(
          JSON.stringify(parsedParams).replaceAll(
            '__applicationId__',
            useAuthStore().user.applicationId
          )
        )
      )
    }

    options = await useDropdownActions()[action](options, parsedParams)
  }

  // finally, parse the options
  selectOptions.value = parseOptions(options, props.options)
  asyncDataToLookupStore(selectOptions.value)

  if (props.defaultValue) {
    /**
     * no need to check for value, text, id or code
     * check what's defined in the structure object.
     * for schema that do not have the structure,
     * follow the default logic.
     */
    if (Object.keys(props.options.structure || {}).length > 0) {
      const { value } = props.options.structure
      selectedValue.value = selectOptions.value.find((o) => o[value] == props.defaultValue)
    } else {
      selectedValue.value = selectOptions.value.find(
        (o) =>
          o.value == props.defaultValue ||
          o.text == props.defaultValue ||
          o.id == props.defaultValue ||
          o.code == props.defaultValue
      )
    }

    if (false && selectedValue.value) {
      textModel.value = selectedValue.value.value
    }
    handleChange(selectedValue?.value?.value)
  }

  /**
   * if there's only one option available, make that the selected option
   */
  if (selectOptions.value.length === 1) {
    selectedValue.value = selectOptions.value[0]
    handleChange(selectedValue?.value?.value)
  }

  fuse.value = new Fuse(selectOptions.value, {
    includeScore: true,
    threshold: 0.2,
    keys: props.fuzzyKeywords
  })
})
onBeforeUnmount(() => {
  cleanupResize()
})

const { errorMessage, handleChange, handleBlur, meta } = useField(
  fieldName,
  async (value) => {
    const result = await useRuleHandler(value, props.rules)
    return result
  },
  {
    initialValue: props.defaultValue
  }
)

const hasError = computed(() => errorMessage.value && meta.touched)

const filteredOptions = computed(() => {
  if (!searchValue.value) return selectOptions.value
  const result = fuse.value.search(searchValue.value)
  return result.map((r) => r.item)
})
const handleResetOthers = () => {
  if (props.resetValues) {
    props.resetValues.forEach((resetValuesKey) => {
      formStore.form.setFieldValue(resetValuesKey, null)
    })
  }
}

const onSelection = async (option) => {
  selectedValue.value = option
  if (false) {
    textModel.value = option?.value
  }
  handleChange(option?.value)
  handleResetOthers()

  if (props.trigger == 'change') {
    await useDropdownActions()[props.onChange.action](option, filteredOptions.value)
  }
}

const t9nLabel = computed(() => i18n.t(props.label))
const t9nPlaceholder = computed(() => props.placeholder && i18n.t(props.placeholder))

// const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))

// const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))
const isRequired = computed(() => {
  const requiredReactive = props.rules?.find((r) => r.name == 'required_reactive')
  if (requiredReactive) {
    return isRequiredByReactive(requiredReactive)
  }
  return !!props.rules?.find((r) => ['required', 'required_ui'].includes(r.name))
})

const isReadOnly = computed(() => {
  if (typeof props.readOnly === 'boolean') {
    return props.readOnly
  } else {
    return false
  }
})

const withIsDisabled = computed(() => {
  if (typeof props.readOnly === 'boolean') {
    return props.readOnly
  }

  if (typeof props.isDisabled === 'boolean') {
    return props.isDisabled
  } else if (props.isDisabled && Object.keys(props.isDisabled).length > 0) {
    const result = findShowRule(
      props.isDisabled.any || props.isDisabled.all,
      props.isDisabled.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )

    return result
  } else {
    return false
  }
})

watchEffect(() => {
  getBindObj(route.params.serviceId)
})

const onValueBlur = () => {
  if (!textModel.value) return
  const parsedText = `${textModel.value}`.toUpperCase()
  const result = selectOptions.value.find((o) => o.value == parsedText)
  if (!result) {
    textModel.value = null
    return
  }

  selectedValue.value = result
  handleChange(result.value.toUpperCase())
  handleResetOthers()
  if (props.onBlur.action) {
    useDropdownActions()[props.onBlur.action]()
  }
}

// triggered when dependentOn is not undefined
// After fetch new options, need reset selectedValue from formStore.
if (formStore?.form?.values) {
  watch(
    () =>
      lodashGet(formStore.form.values, props.dependentOn) ||
      lodashGet(formStore.draft.request, props.dependentOn),
    async (newValue) => {
      formStore.form.resetField(props.fieldName)
      await fetchNewOptions(newValue)
      const currentVal = lodashGet(formStore.form.values, props.fieldName)
      if (currentVal) {
        const result = selectOptions?.value?.find((o) => o.id == currentVal)
        if (result) {
          selectedValue.value = result
        }
      }
    }
  )
}

// Synchronize pinia data and selected values
watch(
  () => lodashGet(formStore.form.values, props.fieldName),
  (val) => {
    if (!val) {
      selectedValue.value = null
    } else {
      const result = selectOptions?.value?.find((o) =>
        [o[props.options?.structure?.value], o.id, o.code].includes(val)
      )
      if (result) {
        selectedValue.value = result
      }
    }
  }
)

async function fetchNewOptions(newValue) {
  if (!newValue) return
  let { path, query, body } = props.options.api
  /**
   * especially when coming from further steps, newValue can become an object
   * so we need to find the ID
   */
  let newValueId
  if (newValue && typeof newValue === 'object') {
    const newValueKeys = Object.keys(newValue)
    newValueId = newValue
    if (Object.keys(newValueKeys).length > 0 && newValueKeys.includes('id')) {
      newValueId = newValue.id
    }
  } else {
    newValueId = newValue
  }

  if (path.includes('__reactive__')) {
    path = path.replaceAll('__reactive__', newValueId)
  }

  const finalQuery = {}
  if (Object.values(query || {}).includes('__reactive__')) {
    const newQuery = JSON.parse(JSON.stringify(query).replaceAll('__reactive__', newValueId))
    Object.assign(finalQuery, newQuery)
  } else {
    Object.assign(finalQuery, query)
  }

  let newPath = path
  if (props.options.api.path_query) {
    newPath += processPathQuery(props.options.api.path_query)
  }
  const newQuery = processQuery(finalQuery)
  const newData = processQuery(body)

  let options = await lookupStore.fetchLookup(
    newPath,
    newQuery,
    {},
    false,
    props.options.api.method,
    newData,
    props.options.api.need_cache
  )

  /**
   * sometimes, extra logic needs to be made.
   * one example is service 767. On change of Person Category,
   * Person Sub Category is fetched, but needs extra handlers to filter out
   * options based on user type, and other factors.
   */
  if (props.options.api.on_success?.action) {
    const { action, action_params } = props.options.api.on_success
    options = await useDropdownActions()[action](options, action_params, newValueId)
  }

  selectOptions.value = parseOptions(options, props.options)
  asyncDataToLookupStore(selectOptions.value)
}

if (props.trigger == 'watch') {
  try {
    useWatchAction()[props.onWatch.action](filteredOptions)
  } catch (error) {
    console.warn(`[DropdownSearch Watch trigger] ${props.onWatch.action} not found.`)
  }
}

if (props.watchAction) {
  try {
    useWatchAction()[props.watchAction](filteredOptions)
  } catch (error) {
    console.warn(`[DropdownSearch WatchAction trigger] ${props.onWatch.action} not found.`)
  }
}

/**
 * provides support for the required rule based on certain values
 * reason:
 * due to sharing forms, it's inevitable to have clashing logic on
 * some of the schema. this is a way to support both while still
 * enabling sharing between services.
 * {
 *     "name": "required_reactive",
 *     "value": {
 *         "operator": "in",
 *         "value": [
 *             33
 *         ],
 *         "ref": "serviceTransactionId"
 *     },
 *     "message": "INVALID_REQUIRED"
 * }
 */
const isRequiredByReactive = (rule) => {
  const { operator, value, ref } = rule.value

  let compareAgainst
  if (ref == 'serviceTransactionId') {
    compareAgainst = useGlobalStore().startedService.serviceTransactionId
  } else {
    compareAgainst = formStore.form.values[ref]
  }

  switch (operator) {
    case 'in':
      return value.includes(compareAgainst)
    case 'not_in':
      return [undefined, ...value].includes(compareAgainst) === false
    case 'not_null':
      return !!compareAgainst
    default:
      return false
  }
}
</script>

<style lang="scss" scoped></style>
